import { useState, useCallback } from 'react';
import { useNotificationActions } from '@/shared/stores/notification-store';

/**
 * Интерфейс для опций обработки отправки формы
 */
interface FormSubmissionOptions<T> {
  onSubmit: (data: T) => Promise<void>;
  successMessage?: string;
  errorMessage?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

/**
 * Результат хука useFormSubmission
 */
interface FormSubmissionResult<T> {
  isSubmitting: boolean;
  handleSubmit: (data: T) => Promise<void>;
  error: string | null;
  clearError: () => void;
}

/**
 * Хук для управления состоянием отправки формы с уведомлениями
 * Предоставляет единообразную обработку загрузки, ошибок и успешных операций
 *
 * @param options - Опции для обработки отправки формы
 * @returns Объект с состоянием и функциями для управления отправкой формы
 */
export const useFormSubmission = <T = any>(
  options: FormSubmissionOptions<T>
): FormSubmissionResult<T> => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useNotificationActions();

  /**
   * Очистить ошибку
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Обработчик отправки формы
   * @param data - Данные формы
   */
  const handleSubmit = useCallback(
    async (data: T): Promise<void> => {
      setIsSubmitting(true);
      setError(null);

      try {
        await options.onSubmit(data);

        // Показываем уведомление об успехе
        if (options.successMessage) {
          showSuccess(options.successMessage);
        }

        // Вызываем callback успеха
        if (options.onSuccess) {
          options.onSuccess();
        }
      } catch (error: any) {
        // Извлекаем сообщение об ошибке
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          options.errorMessage ||
          'Произошла ошибка при выполнении операции';

        setError(errorMessage);

        // Показываем уведомление об ошибке
        showError(errorMessage);

        // Вызываем callback ошибки
        if (options.onError) {
          options.onError(error);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [options, showSuccess, showError]
  );

  return {
    isSubmitting,
    handleSubmit,
    error,
    clearError
  };
};
